import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Camera, Trash2, Save, Lock, ShieldCheck, Bell, WifiOff, Building2, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const BLANK = { name: "", email: "", phone: "", job_title: "", birthday: "", started_at: "", avatar_url: "" };

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [twofa, setTwofa] = useState(false);
  const [savingTwofa, setSavingTwofa] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [notifMeta, setNotifMeta] = useState({ offline_locked: false, assignments: [], role: null });

  useEffect(() => {
    api.get("/auth/me").then((r) => { setForm({ ...BLANK, ...r.data }); setTwofa(!!r.data.twofa_enabled); }).catch(() => {});
    api.get("/me/notification-prefs").then((r) => { setPrefs(r.data.prefs); setNotifMeta({ offline_locked: !!r.data.offline_locked, assignments: r.data.assignments || [], role: r.data.role }); }).catch(() => {});
  }, []);

  const togglePref = async (ntype, channel, val) => {
    const next = { ...prefs, [ntype]: { ...prefs[ntype], [channel]: val } };
    setPrefs(next);
    try {
      await api.put("/me/notification-prefs", { prefs: next });
    } catch {
      toast.error("Could not save notification preference");
      api.get("/me/notification-prefs").then((r) => setPrefs(r.data.prefs)).catch(() => {});
    }
  };

  const isGoogleDomain = (form.email || user?.email || "").toLowerCase().endsWith("@goodnus.com");

  const toggleTwofa = async (enabled) => {
    setSavingTwofa(true);
    try {
      await api.put("/auth/2fa", { enabled });
      setTwofa(enabled);
      if (setUser) setUser((u) => ({ ...u, twofa_enabled: enabled }));
      if (!enabled) localStorage.removeItem("hv_2fa_trust");
      toast.success(enabled ? "Two-factor auth enabled" : "Two-factor auth disabled");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingTwofa(false); }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/auth/me", {
        name: form.name, email: form.email, phone: form.phone,
        job_title: form.job_title, birthday: form.birthday, started_at: form.started_at,
        avatar_url: form.avatar_url,
      });
      setForm({ ...BLANK, ...r.data });
      if (setUser) setUser((u) => ({ ...u, ...r.data }));
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("avatar_url", `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      toast.success("Photo uploaded — remember to save");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const changePw = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.put("/auth/me/password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingPw(false); }
  };

  const initials = (form.name || user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">My profile</h1>
      <p className="text-slate-500 mt-1">Manage your personal information, contact details, avatar and password.</p>

      <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6">
        <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Personal info</h2>
        <div className="flex items-center gap-5 mb-6">
          <div className="relative">
            {form.avatar_url
              ? <img src={form.avatar_url} alt="" className="h-20 w-20 rounded-full object-cover" />
              : <div className="h-20 w-20 rounded-full bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center justify-center text-xl font-bold">{initials}</div>}
            <label className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center cursor-pointer shadow">
              <input data-testid="profile-avatar-upload" type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </label>
          </div>
          {form.avatar_url && (
            <button data-testid="profile-avatar-remove" onClick={() => upd("avatar_url", "")} className="flex items-center gap-1.5 text-sm text-rose-600 hover:underline">
              <Trash2 className="h-4 w-4" /> Remove photo
            </button>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input data-testid="profile-name" value={form.name} onChange={(e) => upd("name", e.target.value)} className="mt-1" /></div>
          <div><Label>Job title</Label><Input data-testid="profile-job-title" value={form.job_title} onChange={(e) => upd("job_title", e.target.value)} placeholder="e.g. Operations Director" className="mt-1" /></div>
          <div><Label>Mobile phone (E.164)</Label><Input data-testid="profile-phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+447700900000" className="mt-1" /></div>
          <div><Label>Birthday</Label><Input data-testid="profile-birthday" value={form.birthday} onChange={(e) => upd("birthday", e.target.value)} placeholder="03-14 (or 1990-03-14)" className="mt-1" /></div>
          <div><Label>Email address</Label><Input data-testid="profile-email" value={form.email} onChange={(e) => upd("email", e.target.value)} className="mt-1" /></div>
          <div><Label>Started at goodnus</Label><Input data-testid="profile-started-at" type="date" value={form.started_at} onChange={(e) => upd("started_at", e.target.value)} className="mt-1" /></div>
        </div>
        <div className="flex justify-end mt-6">
          <Button data-testid="profile-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save changes</>}
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6">
        <h2 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2"><Lock className="h-4 w-4 text-blue-600" /> Change password</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label>Current password</Label><Input data-testid="profile-current-password" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1" /></div>
          <div><Label>New password</Label><Input data-testid="profile-new-password" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1" /></div>
          <div><Label>Confirm new</Label><Input data-testid="profile-confirm-password" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1" /></div>
        </div>
        <div className="flex justify-end mt-6">
          <Button data-testid="profile-change-password" onClick={changePw} disabled={savingPw} variant="outline">
            {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6" data-testid="twofa-card">
        <h2 className="font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Two-factor authentication</h2>
        <p className="text-sm text-slate-500 mb-4">Add a second step at sign-in: after your password you'll confirm your goodnus.com Google account. Trusted devices are remembered for 48 hours.</p>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-white">Require Google verification at login</p>
            <p className="text-xs text-slate-500">{twofa ? "Enabled" : "Disabled"}{!isGoogleDomain && " — requires a goodnus.com account"}</p>
          </div>
          <Switch data-testid="twofa-toggle" checked={twofa} disabled={savingTwofa || !isGoogleDomain} onCheckedChange={toggleTwofa} />
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6" data-testid="notif-prefs-card">
        <h2 className="font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><Bell className="h-4 w-4 text-indigo-600" /> Notifications</h2>
        <p className="text-sm text-slate-500 mb-4">Choose what you're notified about and how. In-app alerts appear in the bell; emails are sent to {form.email || "your address"}.</p>
        {prefs ? (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            <div className="hidden sm:flex items-center px-1 pb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
              <span className="flex-1">Notification</span>
              <span className="w-20 text-center">In-app</span>
              <span className="w-20 text-center">Email</span>
            </div>
            {[
              { key: "purchase", label: "Every purchase", desc: "When a sale is made at a kiosk you can see." },
              { key: "daily_summary", label: "Daily sales summary", desc: "A once-a-day recap of sales, orders and top products." },
              { key: "low_stock", label: "Low stock digest", desc: "Daily list of products at or below their PAR level." },
              { key: "offline", label: "Kiosk offline alerts", desc: "When a kiosk stops responding and needs attention." },
            ].map((row) => {
              const locked = row.key === "offline" && notifMeta.offline_locked;
              return (
              <div key={row.key} className="flex items-center gap-2 py-3" data-testid={`notif-pref-${row.key}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white flex items-center gap-1.5">{row.label}{locked && <Lock className="h-3 w-3 text-amber-500" data-testid="notif-pref-offline-lock" />}</p>
                  <p className="text-xs text-slate-500">{locked ? "You're a kiosk manager for assigned sites, so offline alerts can't be switched off." : row.desc}</p>
                </div>
                <div className="w-20 flex justify-center">
                  <Switch data-testid={`notif-pref-${row.key}-in_app`} disabled={locked} checked={locked ? true : !!prefs[row.key]?.in_app} onCheckedChange={(v) => togglePref(row.key, "in_app", v)} />
                </div>
                <div className="w-20 flex justify-center">
                  <Switch data-testid={`notif-pref-${row.key}-email`} disabled={locked} checked={locked ? true : !!prefs[row.key]?.email} onCheckedChange={(v) => togglePref(row.key, "email", v)} />
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div className="py-6 text-slate-400 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        )}
      </div>

      {notifMeta.assignments.length > 0 && (
        <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6" data-testid="assigned-sites-card">
          <h2 className="font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><Building2 className="h-4 w-4 text-blue-600" /> Assigned sites</h2>
          <p className="text-sm text-slate-500 mb-4">You're a <b>Kiosk Manager</b> for these buildings. You'll always receive offline alerts for their kiosks.</p>
          <div className="space-y-3">
            {notifMeta.assignments.map((a) => (
              <div key={a.building_id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-3" data-testid={`assigned-building-${a.building_id}`}>
                <p className="text-sm font-medium text-slate-900 dark:text-white">{a.building_name}</p>
                {a.kiosks.length ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {a.kiosks.map((k) => (
                      <span key={k.code} className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs text-slate-600 dark:text-slate-300"><Monitor className="h-3 w-3" /> {k.name || k.code}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 mt-1">No kiosks in this building yet.</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
